// cardArt: the ONE catalog-art chain the kiosk screens resolve a card's photo
// through. TKT-2026-107421.
//
// Ported from sortswift (fe) src/utils/cardImage.js: normalizeLang + LANG_ALIASES,
// normalizePrinting, cleanUrl/catalogArtUrl, getPrintingCatalogImage,
// getLocalizedCatalogImage, resolveCardArt, and the seller-scan rule
// (CATALOG_ART_URL / isSellerScanUrl). Only what the kiosk uses is copied. The
// kiosk backend carries the same chain in backend/utils/cardArt.js (CRA cannot
// import from outside src). backend/test/cardArt.portPin.test.js holds both
// copies to one case table and runs fe's own module through it when that
// checkout is on disk; cardArt.test.js here pins the kiosk helpers below and
// utils/images.js. Kept CommonJS with no imports, like fe's own module, so that
// Node test can require it as it is.
//
// Why it exists:
//   - TKT-2026-104063: WOTC-era Pokemon products hold a 1st Edition and an
//     Unlimited run of one card, and TCGplayer publishes ONE render per
//     productId, the STAMPED one. `cdnImage` is therefore the 1st Edition photo.
//     The catalog carries a sparse `printingImages` map keyed by the verbatim
//     printing string ("Unlimited", "Unlimited Holofoil", ...). It never has a
//     "1st Edition" key, because that art already IS `cdnImage`.
//   - TKT-2026-102951: the same map carries "Reverse Holofoil" / "Reverse Holo"
//     keys, because TCGplayer publishes only the regular print. Never
//     special-case a printing name here.
//   - TKT-2026-107421: the kiosk read `printingImages` nowhere, so an Unlimited
//     card was shown, carted and ordered with the 1st Edition photo.
//
// The chain: printing art, then localized art (`langImages`, EN gives ''), then
// `cdnImage`, then ''. A seller's scan of one copy is NOT catalog art and never
// enters it. catalogArtUrl here is cleanUrl alone: the kiosk backend strips the
// registered "no image" placeholders out of cdnImage, printingImages and
// langImages (utils/productImage.js ensureProductImage) before they leave the
// server, and this side has no registry of its own.

// Legacy/junk langAbbr values still on live stock rows, onto the langImages key.
// These are the `folds` of the served language list (sortswift-api GET
// /scanner/config/languages), as fe bundles them in src/data/languageList.snapshot.json
// and cardImage.js folds through them. cardArt.portPin.test.js fails when fe carries a
// fold this table does not: ZH (what the storefront sell form submits), ZH-S, ZH-T and
// EL were missing until 10-16, so those rows drew the English art.
const LANG_ALIASES = {
    JA: 'JP',
    SP: 'ES',
    TW: 'CT',
    CN: 'CS',
    KR: 'KO',
    ZH: 'CS',
    ZHS: 'CS',
    ZHT: 'CT',
    'ZH-S': 'CS',
    'ZH-T': 'CT',
    EL: 'GR',
};

// What counts as the seller's own photo of one copy (fe cardImage.js, the same
// rule as stockScanFilter.js in sortswift-inventory-api and sortswift-api):
//   1. an absolute http(s) URL;
//   2. on our DO Spaces bucket, only `user-uploads/` and `cards/stock-items/`;
//      everything else there (`cards/{productId}.jpg`, `cards/printings/`,
//      Catalog Builder art, generic backs under `assets/`) is catalog art;
//   3. TCGplayer's hosts are catalog art;
//   4. any other host is the seller's photo kept elsewhere.
const CATALOG_ART_URL =
    /^https?:\/\/(?:[^/]*digitaloceanspaces\.com\/(?!(?:user-uploads|cards\/stock-items)\/)|(?:[^/]*\.)?tcgplayer\.com\/)/i;
const IMAGE_URL = /^https?:\/\//i;

/** A langAbbr as the key `langImages` uses, or null. */
function normalizeLang(a) {
    if (!a) return null;
    const upper = String(a).trim().toUpperCase();
    if (!upper) return null;
    return LANG_ALIASES[upper] || upper;
}

/**
 * A printing as the key `printingImages` uses, or null. Trim only: the catalog
 * stores the printing string verbatim, and case is matched at lookup time.
 */
function normalizePrinting(p) {
    if (!p) return null;
    const trimmed = String(p).trim();
    return trimmed || null;
}

/** A value trimmed to a string, else ''. */
function cleanUrl(value) {
    return typeof value === 'string' ? value.trim() : '';
}

/** cleanUrl for a catalog art field (placeholders are stripped server side). */
function catalogArtUrl(value) {
    return cleanUrl(value);
}

/** True when a URL is catalog or generic art rather than a photo the seller took. */
function isCatalogArtUrl(value) {
    const url = cleanUrl(value);
    return !!url && CATALOG_ART_URL.test(url);
}

/** True when a stored value is the seller's own photo of one copy. */
function isSellerScanUrl(value) {
    const url = cleanUrl(value);
    return !!url && IMAGE_URL.test(url) && !isCatalogArtUrl(url);
}

/** The first of `values` that is a seller scan, trimmed, or ''. */
function firstSellerScanUrl(...values) {
    for (const value of values) {
        if (isSellerScanUrl(value)) return cleanUrl(value);
    }
    return '';
}

/**
 * The catalog art for one printing, or ''. Exact key first, then a
 * case-insensitive key, so "unlimited holofoil" from a CSV import still resolves.
 */
function getPrintingCatalogImage(product, printing) {
    const key = normalizePrinting(printing);
    const map = product && product.printingImages;
    if (!key || !map || typeof map !== 'object') return '';

    const exact = catalogArtUrl(map[key]);
    if (exact) return exact;

    const lower = key.toLowerCase();
    for (const mapKey of Object.keys(map)) {
        if (String(mapKey).trim().toLowerCase() === lower) {
            const hit = catalogArtUrl(map[mapKey]);
            if (hit) return hit;
        }
    }
    return '';
}

/** The catalog art in one language, or ''. EN is the default art, so ''. */
function getLocalizedCatalogImage(product, lang) {
    const key = normalizeLang(lang);
    if (!key || key === 'EN') return '';
    return catalogArtUrl(product && product.langImages && product.langImages[key]);
}

/**
 * The catalog art for one printing and language of a product: printing art,
 * else localized art, else `cdnImage`, else ''. With no override, the printing
 * and language are read off `card` itself (fe's scanner card shape).
 */
function resolveCardArt(card, langOverride, printingOverride) {
    if (!card) return '';
    const printing = printingOverride != null && printingOverride !== ''
        ? printingOverride
        : normalizePrinting(card.printingType || (card.skuData && card.skuData.printing) || card.printing);
    const printingArt = getPrintingCatalogImage(card, printing);
    if (printingArt) return printingArt;
    const lang = langOverride != null && langOverride !== ''
        ? langOverride
        : card.language || card.langAbbr;
    const localized = getLocalizedCatalogImage(card, lang);
    if (localized) return localized;
    return catalogArtUrl(card.cdnImage);
}

// ─── Kiosk helpers ───────────────────────────────────────────────────────────

/** The seller's front scan of this copy, or ''. Catalog art parked in cdn_link is not one. */
function variantScanImage(variant) {
    return firstSellerScanUrl(variant && variant.cdn_link);
}

/** The seller's back scan of this copy, or '', held to the same rule. */
function variantBackScanImage(variant) {
    return firstSellerScanUrl(variant && variant.cdn_back_link);
}

/**
 * The catalog art for one copy's own printing and language. The backend sends
 * it resolved as `variant.catalogImage` (routes/products.js); a copy built in
 * the browser (buy mode, a graded slab) is resolved here by the same chain.
 */
function variantCatalogArt(product, variant) {
    const sent = cleanUrl(variant && variant.catalogImage);
    if (sent) return sent;
    return resolveCardArt(product, variant && variant.langAbbr, variant && variant.printing);
}

/** The one value every in-stock copy shares, by `key`, or null. */
function sharedInStock(variants, read, key) {
    let shared = null;
    let sharedKey = null;
    for (const variant of variants) {
        if (!variant || !(Number(variant.quantity) > 0)) continue;
        const value = read(variant);
        if (!value) return null;
        const k = key(value);
        if (sharedKey === null) {
            shared = value;
            sharedKey = k;
        } else if (k !== sharedKey) {
            return null;
        }
    }
    return shared;
}

/**
 * The catalog art for a product shown with no copy chosen: the printing (and
 * language) every in-stock copy shares when there is exactly one, else
 * `cdnImage`.
 */
function productCatalogArt(product) {
    if (!product) return '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const printing = sharedInStock(variants, (v) => normalizePrinting(v.printing), (p) => p.toLowerCase());
    const lang = sharedInStock(variants, (v) => normalizeLang(v.langAbbr), (l) => l);
    return resolveCardArt(product, lang, printing);
}

/**
 * The photo a cart line shows and is ordered with: the seller's scan of the
 * copy, else that copy's catalog art, else what the line was added with.
 */
function cartLineImage(item) {
    if (!item) return '';
    const variant = item.variant;
    return variantScanImage(variant)
        || cleanUrl(variant && variant.catalogImage)
        || cleanUrl(item.cdnImage)
        || cleanUrl(item.catalogImage);
}

module.exports = {
    LANG_ALIASES,
    CATALOG_ART_URL,
    normalizeLang,
    normalizePrinting,
    cleanUrl,
    catalogArtUrl,
    isCatalogArtUrl,
    isSellerScanUrl,
    firstSellerScanUrl,
    getPrintingCatalogImage,
    getLocalizedCatalogImage,
    resolveCardArt,
    variantScanImage,
    variantBackScanImage,
    variantCatalogArt,
    productCatalogArt,
    cartLineImage,
};
